import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';
import { useProducts } from '../hooks/useProducts';

const SearchBar = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('tr') ? 'tr' : 'en';
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const { products: allProducts } = useProducts();

  // Helper to get localized text
  const getText = (field) => {
    if (typeof field === 'object' && field !== null) {
      return field[lang] || field.en || '';
    }
    return field || '';
  };

  // Search function
  const searchProducts = (searchQuery) => {
    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }

    const lowerQuery = searchQuery.toLowerCase();
    
    const filtered = allProducts.filter((product) => {
      // Search by model/product code
      const modelMatch = product.model?.toLowerCase().includes(lowerQuery);
      
      // Search by name
      const nameMatch = product.name?.toLowerCase().includes(lowerQuery);
      
      // Search by display name (both EN and TR)
      const displayNameEN = getText(product.displayName)?.toLowerCase().includes(lowerQuery);
      const displayNameTR = product.displayName?.tr?.toLowerCase().includes(lowerQuery);
      
      // Search by category
      const categoryMatch = product.category?.toLowerCase().includes(lowerQuery);
      
      // Turkish category names
      const categoryTR = {
        'Gaming Desks': 'oyuncu masaları',
        'Monitor Arms': 'monitör kolları',
        'Mobile TV Stands': 'mobil tv standları',
        'TV Stands': 'tv standları',
        'Dyson Stands': 'dyson standları',
        'Accessories': 'aksesuarlar'
      };
      const categoryTRMatch = categoryTR[product.category]?.includes(lowerQuery);

      return modelMatch || nameMatch || displayNameEN || displayNameTR || categoryMatch || categoryTRMatch;
    });

    // Remove duplicates (products that appear in both gaming and office)
    const uniqueResults = filtered.reduce((acc, product) => {
      if (!acc.find(p => p.model === product.model)) {
        acc.push(product);
      }
      return acc;
    }, []);

    setResults(uniqueResults.slice(0, 8)); // Limit to 8 results
  };

  // Handle search input change
  useEffect(() => {
    searchProducts(query);
  }, [query]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Handle keyboard shortcut (Ctrl+K or Cmd+K)
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        setIsOpen(true);
      }
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleResultClick = () => {
    setIsOpen(false);
    setQuery('');
    setResults([]);
  };

  return (
    <div ref={searchRef} className="relative">
      {/* Search Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 transition-colors"
        aria-label="Search"
      >
        <Search className="h-5 w-5" />
        <span className="hidden lg:inline text-sm">{lang === 'tr' ? 'Ara' : 'Search'}</span>
      </button>

      {/* Search Modal/Dropdown */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-[400px] bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden z-50">
          {/* Search Input */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200">
            <Search className="h-5 w-5 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={lang === 'tr' ? 'Ürün kodu, isim veya kategori ara...' : 'Search by product code, name, or category...'}
              className="flex-1 outline-none text-sm text-gray-900 placeholder-gray-500"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="h-4 w-4 text-gray-400" />
              </button>
            )}
          </div>

          {/* Search Results */}
          <div className="max-h-[400px] overflow-y-auto">
            {query && results.length === 0 && (
              <div className="px-4 py-8 text-center text-gray-500 text-sm">
                {lang === 'tr' ? 'Sonuç bulunamadı' : 'No results found'}
              </div>
            )}

            {results.length > 0 && (
              <div className="py-2">
                {results.map((product) => (
                  <Link
                    key={product.id}
                    to={`/product/${product.id}`}
                    onClick={handleResultClick}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
                  >
                    <img
                      src={product.cardImage || product.images?.[0]}
                      alt={getText(product.displayName) || product.name}
                      className="w-12 h-12 object-cover rounded"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-purple-600 font-medium uppercase">
                        {product.model}
                      </p>
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {getText(product.displayName)?.replace(/\n/g, ' ') || product.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {product.category}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {!query && (
              <div className="px-4 py-6 text-center text-gray-500 text-sm">
                <p className="mb-2">{lang === 'tr' ? 'Arama önerileri:' : 'Search suggestions:'}</p>
                <div className="flex flex-wrap justify-center gap-2">
                  {['MA13', 'GD-12002', 'PS5', lang === 'tr' ? 'Monitör' : 'Monitor', lang === 'tr' ? 'Oyuncu' : 'Gaming'].map((suggestion) => (
                    <button
                      key={suggestion}
                      onClick={() => setQuery(suggestion)}
                      className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 border-t border-gray-200 bg-gray-50 text-xs text-gray-500 flex items-center justify-between">
            <span>{lang === 'tr' ? 'Kapatmak için ESC' : 'ESC to close'}</span>
            <span>{results.length > 0 ? `${results.length} ${lang === 'tr' ? 'sonuç' : 'results'}` : ''}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
